const MAX_POLYGONS_FOR_LAYERS = 12;

function signedArea(polygon) {
    if (!Array.isArray(polygon) || polygon.length < 3) return 0;
    let area = 0;
    for (let i = 0; i < polygon.length; i += 1) {
        const j = (i + 1) % polygon.length;
        area += polygon[i].lng * polygon[j].lat - polygon[j].lng * polygon[i].lat;
    }
    return area / 2;
}

function pointInPolygon(point, polygon) {
    let inside = false;
    const n = polygon.length;
    for (let i = 0, j = n - 1; i < n; j = i, i += 1) {
        const xi = polygon[i].lng;
        const yi = polygon[i].lat;
        const xj = polygon[j].lng;
        const yj = polygon[j].lat;
        const intersect = ((yi > point.lat) !== (yj > point.lat))
            && (point.lng < ((xj - xi) * (point.lat - yi)) / ((yj - yi) || 1e-12) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}

function segmentIntersection(a1, a2, b1, b2) {
    const x1 = a1.lng;
    const y1 = a1.lat;
    const x2 = a2.lng;
    const y2 = a2.lat;
    const x3 = b1.lng;
    const y3 = b1.lat;
    const x4 = b2.lng;
    const y4 = b2.lat;
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 1e-15) return null;
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
    if (t < 0 || t > 1 || u < 0 || u > 1) return null;
    return { lat: y1 + t * (y2 - y1), lng: x1 + t * (x2 - x1) };
}

function polygonCentroid(polygon) {
    if (!polygon?.length) return null;
    return {
        lat: polygon.reduce((s, p) => s + p.lat, 0) / polygon.length,
        lng: polygon.reduce((s, p) => s + p.lng, 0) / polygon.length,
    };
}

export function polygonsOverlap(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length < 3 || b.length < 3) return false;

    const ca = polygonCentroid(a);
    const cb = polygonCentroid(b);
    if (ca && pointInPolygon(ca, b)) return true;
    if (cb && pointInPolygon(cb, a)) return true;

    for (let i = 0; i < a.length; i += 1) {
        const a1 = a[i];
        const a2 = a[(i + 1) % a.length];
        for (let j = 0; j < b.length; j += 1) {
            const b1 = b[j];
            const b2 = b[(j + 1) % b.length];
            if (segmentIntersection(a1, a2, b1, b2)) return true;
        }
    }
    return false;
}

/**
 * Greedy-color polygons into non-overlapping layers (parallel index array).
 */
export function assignLayers(polygons) {
    if (!Array.isArray(polygons) || polygons.length === 0) return [];
    if (polygons.length > MAX_POLYGONS_FOR_LAYERS) {
        return polygons.map(() => 0);
    }

    const order = polygons
        .map((p, i) => ({ i, area: signedArea(p) }))
        .sort((a, b) => Math.abs(b.area) - Math.abs(a.area))
        .map((o) => o.i);

    const assigned = new Array(polygons.length).fill(null);

    for (const i of order) {
        for (let layer = 0; ; layer += 1) {
            const collision = polygons.some((p, j) =>
                j !== i && assigned[j] === layer && polygonsOverlap(polygons[i], p));
            if (!collision) {
                assigned[i] = layer;
                break;
            }
        }
    }
    return assigned;
}
